import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Phone, Microphone, StopCircle, PlayCircle, ArrowRight, SpinnerGap } from "@phosphor-icons/react";

const COLS = ["Current Node", "VAD Event", "Transcript", "Intent", "Score", "Branch", "Time (s)"];

export default function TestCallModal({ campaign, open, onClose }) {
  const [session, setSession] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("idle");
  const [transfer, setTransfer] = useState(null);
  const [scenario, setScenario] = useState("interested");
  const [transferSuccess, setTransferSuccess] = useState(true);
  const [live, setLive] = useState(false);
  const [listening, setListening] = useState(false);
  const wsRef = useRef(null);
  const mediaRef = useRef(null);
  const audioElRef = useRef(null);
  const pendingSttRef = useRef(null);
  const lastTsRef = useRef(null);

  useEffect(() => {
    if (open && campaign) startSession();
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaign]);

  const cleanup = () => {
    try { wsRef.current?.close(); } catch { /* noop */ }
    try { mediaRef.current?.stop?.(); } catch { /* noop */ }
    wsRef.current = null; mediaRef.current = null;
  };

  const startSession = async () => {
    setRows([]); setTransfer(null); setStatus("starting");
    try {
      const { data } = await api.post("/test-call/start", { campaign_id: campaign.id });
      setSession(data); setStatus("ready");
    } catch (e) {
      setStatus("error"); toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Seconds each step took: for a wait→speech row this is the human's
  // response+speech time; for a play row it's the bot's prompt time.
  const pushDebug = (m) => setRows((r) => {
    const now = performance.now();
    const dt = lastTsRef.current != null ? (now - lastTsRef.current) / 1000 : null;
    lastTsRef.current = now;
    const row = {
      node: m.current_node, kind: m.kind, vad: m.vad_event,
      intent: m.intent, score: m.score, branch: m.branch, transcript: null,
      time: dt, human_s: null,
    };
    // The stt (transcript) message can arrive just before this wait row is
    // created — attach the buffered transcript so it isn't lost.
    if (m.kind === "wait" && pendingSttRef.current) {
      row.transcript = pendingSttRef.current.transcript;
      row.human_s = pendingSttRef.current.speech_duration ?? null;
      pendingSttRef.current = null;
    }
    return [...r, row];
  });

  const applyStt = (m) => {
    // Buffer for the (possibly not-yet-created) wait row, and also patch the
    // most recent wait row if it already exists.
    pendingSttRef.current = { transcript: m.transcript, speech_duration: m.speech_duration };
    setRows((r) => {
      const c = [...r];
      for (let i = c.length - 1; i >= 0; i -= 1) {
        if (c[i].kind === "wait" && !c[i].transcript) {
          c[i] = { ...c[i], transcript: m.transcript, human_s: m.speech_duration ?? c[i].human_s };
          pendingSttRef.current = null;
          break;
        }
      }
      return c;
    });
  };

  const runSimulation = async () => {
    if (!session) return;
    setRows([]); setTransfer(null); setStatus("running");
    try {
      const { data } = await api.post(`/test-call/${session.session_id}/simulate`,
        { scenario, transfer_success: transferSuccess });
      const merged = [];
      let ti = 0;
      data.debug.forEach((d) => {
        const row = { node: d.current_node, kind: d.kind, vad: d.vad_event,
          intent: d.intent, score: d.score, branch: d.branch, transcript: null,
          time: null, human_s: null };
        if (d.kind === "wait" && data.turns[ti]) {
          const t = data.turns[ti];
          row.transcript = t.transcript;
          row.human_s = t.speech_duration ?? null;
          row.time = t.processing_time ?? t.speech_duration ?? null;
          ti += 1;
        }
        merged.push(row);
      });
      setRows(merged);
      setTransfer(data.test_transfer);
      setStatus("done");
    } catch (e) {
      setStatus("error"); toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downsample = (input, inRate, outRate = 16000) => {
    if (inRate === outRate) return input;
    const ratio = inRate / outRate;
    const outLen = Math.floor(input.length / ratio);
    const out = new Float32Array(outLen);
    for (let i = 0; i < outLen; i += 1) out[i] = input[Math.floor(i * ratio)] || 0;
    return out;
  };

  const startLive = async () => {
    if (!session) return;
    setRows([]); setTransfer(null); setStatus("live"); setLive(true); setListening(false);
    pendingSttRef.current = null; lastTsRef.current = performance.now();
    const token = localStorage.getItem("wa_token");
    const wsUrl = `${API.replace(/^http/, "ws")}/test-call/ws/${session.session_id}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      const m = JSON.parse(ev.data);
      if (m.type === "listening") { setListening(true); return; }
      setListening(false);
      if (m.type === "debug") pushDebug(m);
      else if (m.type === "stt") applyStt(m);
      else if (m.type === "play") playNode(m);
      else if (m.type === "done") { setTransfer(m.test_transfer); setStatus("done"); setLive(false); stopMic(); }
      else if (m.type === "error") { toast.error(formatApiError(m.detail)); setStatus("error"); setLive(false); }
    };
    ws.onerror = () => { toast.error("Test-call connection failed"); setLive(false); };
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      try { await ctx.resume(); } catch { /* noop */ }
      const source = ctx.createMediaStreamSource(stream);
      const proc = ctx.createScriptProcessor(4096, 1, 1);
      proc.onaudioprocess = (e) => {
        if (ws.readyState !== 1) return;
        const frame = downsample(e.inputBuffer.getChannelData(0), ctx.sampleRate, 16000);
        ws.send(frame.buffer);
      };
      // Route through a muted gain so the processor keeps firing without echoing
      // the mic back to the speakers.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      source.connect(proc); proc.connect(sink); sink.connect(ctx.destination);
      let stopped = false;
      mediaRef.current = { stop: () => {
        if (stopped) return;
        stopped = true;
        try { proc.disconnect(); source.disconnect(); sink.disconnect(); } catch { /* noop */ }
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
        try { if (ctx.state !== "closed") ctx.close(); } catch { /* noop */ }
      } };
    } catch (err) {
      toast.error("Microphone permission denied — use Simulate instead");
      setLive(false);
    }
  };

  const stopMic = () => { try { mediaRef.current?.stop?.(); } catch { /* noop */ } mediaRef.current = null; };

  const playNode = (m) => {
    if (m.mode === "audio" && m.audio_file_id) {
      const token = localStorage.getItem("wa_token");
      if (audioElRef.current) { audioElRef.current.src = `${API}/audio/${m.audio_file_id}?auth=${token}`; audioElRef.current.play().catch(() => {}); }
    } else if (m.mode === "text" && m.prompt_text && window.speechSynthesis) {
      try { window.speechSynthesis.speak(new SpeechSynthesisUtterance(m.prompt_text)); } catch { /* noop */ }
    }
  };

  const endLive = () => { try { wsRef.current?.send(JSON.stringify({ type: "end" })); } catch { /* noop */ } stopMic(); setLive(false); setListening(false); ws_close(); };
  const ws_close = () => { try { wsRef.current?.close(); } catch { /* noop */ } };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { cleanup(); onClose(); } }}>
      <DialogContent className="max-w-3xl" data-testid="test-call-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Phone className="w-5 h-5" weight="fill" /> Test Call — {campaign?.name}</DialogTitle>
          <DialogDescription>Isolated test run. No wallet, CDR, real SIP/DID calls or production limits are affected.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={scenario} onValueChange={setScenario} disabled={live}>
            <SelectTrigger className="w-52" data-testid="test-scenario-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="interested">Says "yes / interested"</SelectItem>
              <SelectItem value="not_interested">Says "no / not interested"</SelectItem>
              <SelectItem value="unknown">Says something unknown</SelectItem>
              <SelectItem value="silent">Stays silent</SelectItem>
              <SelectItem value="agent">Requests an agent</SelectItem>
              <SelectItem value="dtmf">Presses DTMF "1"</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={runSimulation} disabled={!session || live || status === "running"} className="rounded-lg" data-testid="test-simulate-btn">
            <PlayCircle className="w-4 h-4 mr-1.5" weight="fill" /> {status === "running" ? "Running…" : "Simulate"}
          </Button>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground select-none cursor-pointer" data-testid="test-transfer-toggle-label">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-primary cursor-pointer"
              checked={transferSuccess}
              disabled={live}
              onChange={(e) => setTransferSuccess(e.target.checked)}
              data-testid="test-transfer-success-toggle"
            />
            Transfer succeeds
          </label>
          {!live ? (
            <Button variant="outline" onClick={startLive} disabled={!session} className="rounded-lg" data-testid="test-live-btn">
              <Microphone className="w-4 h-4 mr-1.5" weight="fill" /> Live mic call
            </Button>
          ) : (
            <Button variant="destructive" onClick={endLive} className="rounded-lg" data-testid="test-end-btn">
              <StopCircle className="w-4 h-4 mr-1.5" weight="fill" /> End call
            </Button>
          )}
          {live && (
            listening ? (
              <span className="flex items-center gap-1.5 text-sm font-medium text-primary animate-pulse" data-testid="test-listening-indicator">
                <Microphone className="w-4 h-4" weight="fill" /> Listening — please speak
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="test-speaking-indicator">
                <PlayCircle className="w-4 h-4" weight="fill" /> Bot speaking…
              </span>
            )
          )}
          <span className="text-xs text-muted-foreground ml-auto" data-testid="test-status">Status: {status}</span>
        </div>

        {transfer && (
          <div className={`rounded-lg px-3 py-2 text-sm font-medium ${transfer === "SUCCESS" ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"}`} data-testid="test-transfer-result">
            TEST TRANSFER ← {transfer}
          </div>
        )}

        <div className="rounded-xl border border-border overflow-hidden max-h-[46vh] overflow-y-auto" data-testid="test-debug-log">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 sticky top-0">
              <tr>{COLS.map((c) => <th key={c} className="text-left px-3 py-2 font-medium">{c}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length === 0 && status !== "running" && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Pick a scenario and Simulate, or start a Live mic call.</td></tr>}
              {rows.length === 0 && status === "running" && (
                <tr data-testid="test-loading-row"><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  <span className="inline-flex items-center gap-2"><SpinnerGap className="w-4 h-4 animate-spin" /> Running the flow through the Voice Bot engine… (first run loads the STT model, 15–40s)</span>
                </td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border" data-testid={`test-row-${i}`}>
                  <td className="px-3 py-1.5 font-medium">{r.node} <span className="text-muted-foreground">({r.kind})</span></td>
                  <td className="px-3 py-1.5">{r.vad || "—"}</td>
                  <td className="px-3 py-1.5 max-w-[180px] truncate" title={r.transcript || undefined}>{r.transcript || "—"}</td>
                  <td className="px-3 py-1.5">{r.intent || "—"}</td>
                  <td className="px-3 py-1.5 tabular-nums">{r.score != null ? r.score : "—"}</td>
                  <td className="px-3 py-1.5">{r.branch ? <span className="inline-flex items-center gap-1 text-primary"><ArrowRight className="w-3 h-3" />{r.branch}</span> : "—"}</td>
                  <td className="px-3 py-1.5 tabular-nums text-muted-foreground whitespace-nowrap" data-testid={`test-row-${i}-time`}>
                    {r.time != null ? `${r.time.toFixed(1)}s` : "—"}
                    {r.human_s != null ? <span className="text-primary"> · spoke {r.human_s.toFixed(1)}s</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <audio ref={audioElRef} className="hidden" />
      </DialogContent>
    </Dialog>
  );
}
